import axios, { CancelToken, CancelTokenSource } from 'axios';
import { useCallback, useEffect, useState, useRef } from 'react';

type CacheEntry<T> = {
  data: T;
  timestamp: number;
};

export type QueryKey =
  | string
  | null
  | (string | number | boolean | null | undefined)[]
  | Record<string, any>;

type QueryFunction<T> = (
  queryKey: QueryKey,
  cancelToken?: CancelToken
) => Promise<T>;

type UseQueryOptions = {
  cacheTTL?: number; // Time to live in milliseconds
  cacheKeyPrefix?: string; // Prefix for cache keys to avoid collisions
  /** When false, the query does not run automatically; use `refetch()` to fetch. Defaults to true. */
  autoLoadingEnabled?: boolean;
};

type UseQueryReturn<T> = {
  data: T;
  errors: string | null;
  isLoading: boolean;
  refreshCache: (data: T) => void;
  refetch: () => Promise<void>;
};

// Global cache storage - using a Map for better performance
const globalCache = new Map<string, CacheEntry<any>>();

// Serialize query key to string for cache key
const serializeQueryKey = (key: QueryKey): string => {
  if (key === null || key === undefined) {
    return '';
  }
  if (typeof key === 'string') {
    return key;
  }
  if (Array.isArray(key)) {
    return JSON.stringify(key);
  }
  if (typeof key === 'object') {
    return JSON.stringify(key);
  }
  return String(key);
};

const useQuery = <T>(
  queryKey: QueryKey,
  queryFn: QueryFunction<T>,
  options: UseQueryOptions = {}
): UseQueryReturn<T> => {
  const {
    cacheTTL = 3 * 60 * 1000, // Default 3 minutes
    cacheKeyPrefix = '',
    autoLoadingEnabled = true
  } = options;

  const getCacheKey = useCallback(
    (key: QueryKey) => {
      const serialized = serializeQueryKey(key);
      return cacheKeyPrefix ? `${cacheKeyPrefix}:${serialized}` : serialized;
    },
    [cacheKeyPrefix]
  );

  const [data, setData] = useState<T>({} as T);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string | null>(null);

  const mounted = useRef(true);
  const currentRequestId = useRef<string | null>(null);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const isRequestValid = useCallback((requestId: string) => {
    return currentRequestId.current === requestId && mounted.current;
  }, []);

  const handleRequestError = useCallback((error: Error) => {
    if (error instanceof Error && error.name !== 'AbortError') {
      setErrors(error.message);
    } else if (typeof error === 'string' && error !== 'AbortError') {
      setErrors(error);
    }
    setIsLoading(false);
  }, []);

  const handleRequestSuccess = useCallback(
    (response: T, now: number, key: QueryKey) => {
      const cacheKey = getCacheKey(key);
      globalCache.set(cacheKey, { data: response, timestamp: now });
      setData(response);
      setIsLoading(false);
    },
    [getCacheKey]
  );

  const handleCachedData = useCallback((cached: CacheEntry<T>) => {
    setData(cached.data);
    setIsLoading(false);
  }, []);

  const refreshCache = useCallback(
    (newData: T) => {
      if (queryKey) {
        const cacheKey = getCacheKey(queryKey);
        globalCache.set(cacheKey, { data: newData, timestamp: Date.now() });
        setData(newData);
      }
    },
    [queryKey, getCacheKey]
  );

  const loadData = useCallback(
    async (bypassCache: boolean = false) => {
      if (queryKey === null || queryKey === undefined || queryKey === '') {
        setData({} as T);
        setIsLoading(false);
        setErrors(null);
        return;
      }

      const serializedKey = serializeQueryKey(queryKey);
      const requestId = `${serializedKey}-${Date.now()}`;
      currentRequestId.current = requestId;

      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Operation canceled due to new request.');
      }

      cancelTokenRef.current = axios.CancelToken.source();

      const now = Date.now();
      const cacheKey = getCacheKey(queryKey);
      const cached = globalCache.get(cacheKey);

      if (!bypassCache && cached && now - cached.timestamp < cacheTTL) {
        if (isRequestValid(requestId)) {
          handleCachedData(cached);
        }
        return;
      }

      if (mounted.current) {
        setIsLoading(true);
        setErrors(null);
      }

      try {
        const response = await queryFn(queryKey, cancelTokenRef.current.token);

        if (isRequestValid(requestId)) {
          handleRequestSuccess(response, now, queryKey);
        }
      } catch (error) {
        if (isRequestValid(requestId)) {
          handleRequestError(error as Error);
        }
      }
    },
    [
      queryKey,
      queryFn,
      cacheTTL,
      getCacheKey,
      isRequestValid,
      handleCachedData,
      handleRequestSuccess,
      handleRequestError
    ]
  );

  const refetch = useCallback(async () => {
    await loadData(true); // Bypass cache
  }, [loadData]);

  useEffect(() => {
    mounted.current = true;
    if (autoLoadingEnabled) {
      loadData();
    }
    return () => {
      mounted.current = false;
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted, request canceled.');
      }
    };
  }, [loadData, autoLoadingEnabled]);

  return { data, errors, isLoading, refreshCache, refetch };
};

export default useQuery;
